import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'app/hooks'
import Avatar from 'components/avatar/Avatar';
import AccountSettings from 'features/account-settings/AccountSettings'
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { useLogoUrl } from 'utils/hooks/useLogoUrl';
import { useTranslation } from 'react-i18next';
import { NAVIGATION, TITLES } from 'locales/locales';

interface HeaderProps {
  //currentUser: IUser | null;
}

const Header: React.FC<HeaderProps> = () => {
  const currentUser = useAppSelector((state) => state.auth.user)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const systemSettings = useAppSelector((state) => state.system.data)
  const { logoUrl, logoRef } = useLogoUrl(systemSettings?.imageUrlLogo)
  
  // Dropdown dışına tıklandığında menüyü kapat
  useClickOutside(dropdownRef, () => setIsDropdownOpen(false))

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const onCloseAccountSettings = () => {
    setIsAccountSettingsOpen(false)
  }

  const handleProfileClick = () => {
    setIsDropdownOpen(false)
    setIsAccountSettingsOpen(true)
  }

  const handleHomeClick = () => {
    setIsDropdownOpen(false)
    // Ana sayfaya yönlendir
    navigate('/admin-panel/dashboard/general-view')
  }

  const handleLogoutClick = () => {
    setIsDropdownOpen(false)
    // Çıkış işlemi
    // logout fonksiyonunu çağır
    navigate('/')
  }

  const handleLogoClick = () => {
    setIsDropdownOpen(false)
    setIsAccountSettingsOpen(false)
    navigate('/news-feed')
  }

  return (
    <TopNavbar>
      <NavbarLogoSection>
        <StyledLogo ref={logoRef} src={logoUrl} alt="logo" onClick={handleLogoClick}/>
      </NavbarLogoSection>
      <NavbarTitleSection>
        <VimesoftTitle>{systemSettings?.panelName}</VimesoftTitle>
      </NavbarTitleSection>
      <NavbarActionsSection>
        <UserDropdownContainer ref={dropdownRef}>
          <UserInfo onClick={handleDropdownToggle}>
            <Avatar
              size={32}
              source={currentUser?.avatar}
              type={'circle'}
              text={currentUser?.fullname}
              color={'#54595D'}
            />
            <UserName>{currentUser?.fullname}</UserName>
            <DropdownArrow isOpen={isDropdownOpen}>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </DropdownArrow>
          </UserInfo>

          {isDropdownOpen && (
            <DropdownMenu>
              <DropdownItem onClick={handleProfileClick}>
                {t(NAVIGATION.ACCOUNT_SETTINGS)}
              </DropdownItem>
              <DropdownItem onClick={handleHomeClick}>{t(TITLES.DASHBOARD_TITLE)}</DropdownItem>
              <DropdownItem onClick={handleLogoutClick}>{t(NAVIGATION.BACK_TO_APP)}</DropdownItem>
            </DropdownMenu>
          )}
        </UserDropdownContainer>
      </NavbarActionsSection>

      {/* Account Settings Modal */}
      {isAccountSettingsOpen && <AccountSettings onClose={onCloseAccountSettings} />}
    </TopNavbar>
  )
};

export default Header;

const TopNavbar = styled.div`
  display: flex;
  align-items: center;
  height: 62px;
  background-color: white;
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const NavbarLogoSection = styled.div`
  padding: 0 15px;
  background-color: white;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLogo = styled.img`
  margin-left: 10px;
  height: 80%;
  width: 80%;
  object-fit: contain;
  cursor: pointer;
`
const NavbarTitleSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const NavbarActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  color: #333;
  position: relative;
`;

const VimesoftTitle = styled.h1`
  font-size: 16px;
  font-weight: 650;
  font-family: 'Public Sans', sans-serif;
  margin: 0;
  color: #333;
`;

const UserDropdownContainer = styled.div`
  position: relative;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const DropdownArrow = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: #666;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

